import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import {
  fetchStatsDashboard,
  fetchStatsChart,
  fetchStatsHot,
  fetchStatsHourly
} from '../services/api';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

const RANGE_OPTIONS = [
  { label: '12h', value: 12 },
  { label: '24h', value: 24 },
  { label: '48h', value: 48 },
  { label: '7 dni', value: 168 },
  { label: '30 dni', value: 720 }
];

const PERIOD_OPTIONS = [
  { label: 'Dzień', value: 'day' },
  { label: 'Tydzień', value: 'week' },
  { label: 'Miesiąc', value: 'month' }
];

// eslint-disable-next-line no-unused-vars
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDuration = (seconds) => {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatTime = (str) => {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatChartTime = (str, hours) => {
  if (!str) return '';
  const d = new Date(str);
  if (hours <= 48) return d.toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit' });
};

// Pill button component
const Pill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
      active
        ? 'bg-primary text-white shadow-lg shadow-primary/30'
        : 'bg-surface-dark text-slate-400 hover:text-white border border-surface-border hover:border-primary/50'
    }`}
  >
    {children}
  </button>
);

// Card wrapper
const Card = ({ title, icon, children, className = '' }) => (
  <div className={`bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-surface-border shadow-xl p-6 ${className}`}>
    {title && (
      <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white mb-4">
        {icon && <span className="material-symbols-outlined text-primary">{icon}</span>}
        {title}
      </h3>
    )}
    {children}
  </div>
);



// Chart component for time series
const TimeSeriesChart = ({ credentials, eventType, title, icon, color }) => {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStatsChart(credentials, eventType, hours)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [credentials, eventType, hours]);

  const chartData = data.map(d => ({
    time: formatChartTime(d.time_bucket, hours),
    count: d.count
  }));

  return (
    <Card title={title} icon={icon}>
      <div className="flex gap-2 mb-4 flex-wrap">
        {RANGE_OPTIONS.map(opt => (
          <Pill key={opt.value} active={hours === opt.value} onClick={() => setHours(opt.value)}>
            {opt.label}
          </Pill>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-500">Ładowanie...</div>
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-slate-500">Brak danych</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#232f48" />
            <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#1a2333', border: '1px solid #232f48', borderRadius: 8, color: '#fff' }}
              labelStyle={{ color: '#94a3b8' }}
            />
            <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2} dot={false} name="Ilość" />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

// Hourly activity bar chart
const HourlyActivityChart = ({ credentials }) => {
  const [allData, setAllData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load all 7 days data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStatsHourly(credentials)
      .then(d => {
        if (cancelled) return;
        setAllData(d);
        const dates = [...new Set(d.map(r => {
          const dateVal = r.date;
          return typeof dateVal === 'string' ? dateVal.split('T')[0] : new Date(dateVal).toISOString().split('T')[0];
        }))].sort();
        setAvailableDates(dates);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [credentials]);

  // Build chart data
  const chartData = React.useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${String(i).padStart(2, '0')}:00`, count: 0 }));

    const filtered = selectedDate
      ? allData.filter(r => {
          const dateVal = r.date;
          const dateStr = typeof dateVal === 'string' ? dateVal.split('T')[0] : new Date(dateVal).toISOString().split('T')[0];
          return dateStr === selectedDate;
        })
      : allData;

    filtered.forEach(r => {
      const idx = r.hour;
      if (idx >= 0 && idx < 24) {
        hours[idx].count += r.count;
      }
    });

    return hours;
  }, [allData, selectedDate]);

  const formatDateLabel = (d) => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('pl-PL', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  return (
    <Card title="Aktywność godzinowa (ostatnie 7 dni)" icon="schedule">
      <div className="flex gap-2 mb-4 flex-wrap">
        <Pill active={selectedDate === null} onClick={() => setSelectedDate(null)}>
          Wszystkie
        </Pill>
        {availableDates.map(d => (
          <Pill key={d} active={selectedDate === d} onClick={() => setSelectedDate(d)}>
            {formatDateLabel(d)}
          </Pill>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-500">Ładowanie...</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#232f48" />
            <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 11 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#1a2333', border: '1px solid #232f48', borderRadius: 8, color: '#fff' }}
              labelStyle={{ color: '#94a3b8' }}
            />
            <Bar dataKey="count" fill="#135bec" radius={[4, 4, 0, 0]} name="Zdarzenia" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

// Song table
const SongTable = ({ songs, title, icon, timeLabel }) => (
  <Card title={title} icon={icon}>
    {songs.length === 0 ? (
      <p className="text-slate-500 text-sm">Brak danych</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-surface-border">
              <th className="pb-2 font-medium">#</th>
              <th className="pb-2 font-medium">Tytuł</th>
              <th className="pb-2 font-medium">Czas</th>
              <th className="pb-2 font-medium">{timeLabel}</th>
            </tr>
          </thead>
          <tbody>
            {songs.map((song, idx) => (
              <tr key={idx} className="border-b border-gray-100 dark:border-surface-border last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-border/30 transition-colors">
                <td className="py-2.5 text-slate-500">{idx + 1}</td>
                <td className="py-2.5 text-slate-900 dark:text-white font-medium max-w-[200px] truncate">{song.title || song.video_id}</td>
                <td className="py-2.5 text-slate-400">{formatDuration(song.duration)}</td>
                <td className="py-2.5 text-slate-400">{formatTime(song.event_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

// Hot songs table
const HotSongsTable = ({ credentials }) => {
  const [period, setPeriod] = useState('day');
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStatsHot(credentials, period)
      .then(d => { if (!cancelled) { setSongs(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [credentials, period]);

  return (
    <Card title="Najgorętsze hity" icon="local_fire_department">
      <div className="flex gap-2 mb-4">
        {PERIOD_OPTIONS.map(opt => (
          <Pill key={opt.value} active={period === opt.value} onClick={() => setPeriod(opt.value)}>
            {opt.label}
          </Pill>
        ))}
      </div>
      {loading ? (
        <p className="text-slate-500 text-sm">Ładowanie...</p>
      ) : songs.length === 0 ? (
        <p className="text-slate-500 text-sm">Brak danych</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-surface-border">
                <th className="pb-2 font-medium">#</th>
                <th className="pb-2 font-medium">Tytuł</th>
                <th className="pb-2 font-medium">Operacje</th>
              </tr>
            </thead>
            <tbody>
              {songs.map((song, idx) => (
                <tr key={idx} className="border-b border-gray-100 dark:border-surface-border last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-border/30 transition-colors">
                  <td className="py-2.5">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      idx === 1 ? 'bg-slate-400/20 text-slate-400' :
                      idx === 2 ? 'bg-amber-700/20 text-amber-600' :
                      'bg-surface-border text-slate-500'
                    }`}>
                      {idx + 1}
                    </span>
                  </td>
                  <td className="py-2.5 text-slate-900 dark:text-white font-medium max-w-[250px] truncate">{song.title || song.video_id}</td>
                  <td className="py-2.5">
                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-bold">{song.count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// Login form
const LoginForm = ({ onLogin, error, loading }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!loading) onLogin({ username, password });
  };

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col w-full">
      <Header />
      <main className="flex-grow flex items-center justify-center px-4 py-12">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(500px,100vw)] h-[min(500px,100vh)] bg-primary/20 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
        <div className="w-full max-w-sm">
          <Card title="Logowanie" icon="lock">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Login</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-gray-100 dark:bg-background-dark border border-gray-200 dark:border-surface-border rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Hasło</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-100 dark:bg-background-dark border border-gray-200 dark:border-surface-border rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:border-primary"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-2.5 px-6 rounded-lg transition-all duration-200 shadow-lg shadow-primary/30 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logowanie...' : 'Zaloguj się'}
              </button>
            </form>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Main stats page
const StatsPage = () => {
  const [credentials, setCredentials] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Attempt login
  const handleLogin = useCallback(async (creds) => {
    setLoginError('');
    setLoginLoading(true);
    try {
      console.log('[StatsPage] Attempting login...');
      const data = await fetchStatsDashboard(creds);
      console.log('[StatsPage] Dashboard data received:', data);
      setCredentials(creds);
      setDashboard(data);
    } catch (err) {
      console.error('[StatsPage] Login error:', err);
      console.error('[StatsPage] Error response:', err.response);
      console.error('[StatsPage] Error message:', err.message);
      if (err.response?.status === 401) {
        setLoginError('Nieprawidłowy login lub hasło');
      } else if (err.response?.status >= 500) {
        setLoginError('Błąd serwera: ' + (err.response?.data?.error || 'Spróbuj ponownie'));
      } else {
        setLoginError('Błąd połączenia: ' + (err.message || 'Sprawdź konsolę przeglądarki'));
      }
    } finally {
      setLoginLoading(false);
    }
  }, []);

  // Reload dashboard data
  const refreshDashboard = useCallback(async () => {
    if (!credentials) return;
    setLoading(true);
    try {
      const data = await fetchStatsDashboard(credentials);
      setDashboard(data);
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
    }
    setLoading(false);
  }, [credentials]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!credentials) return;
    const interval = setInterval(refreshDashboard, 60000);
    return () => clearInterval(interval);
  }, [credentials, refreshDashboard]);

  if (!credentials) {
    return <LoginForm onLogin={handleLogin} error={loginError} loading={loginLoading} />;
  }

  if (!dashboard) {
    return (
      <div className="bg-background-light dark:bg-background-dark min-h-screen flex items-center justify-center">
        <div className="text-white text-lg">Ładowanie...</div>
      </div>
    );
  }

  const { songs, events, lastDownloaded, lastConverted } = dashboard;

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col w-full overflow-x-hidden">
      <Header />

      <main className="flex-grow w-full px-4 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-3xl">analytics</span>
              Statystyki
            </h1>
            <button
              onClick={refreshDashboard}
              disabled={loading}
              className="flex items-center gap-2 bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border text-slate-600 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:border-primary/50 transition-colors disabled:opacity-50"
            >
              <span className={`material-symbols-outlined text-sm ${loading ? 'animate-spin' : ''}`}>refresh</span>
              Odśwież
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary">music_note</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Ilość piosenek</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">{songs.total}</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-green-500">hard_drive</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Całkowity rozmiar</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">{songs.totalSizeFormatted}</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-purple-500">equalizer</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Średni rozmiar</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">{songs.avgSizeFormatted}</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-amber-500">monitoring</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Wszystkie zdarzenia</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">{events.totalEvents}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Event summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Konwersje', key: 'conversion_complete', icon: 'swap_horiz', color: 'text-blue-400' },
              { label: 'Pobrania', key: 'download', icon: 'download', color: 'text-green-400' },
              { label: 'Wyświetlenia', key: 'page_view', icon: 'visibility', color: 'text-purple-400' },
              { label: 'Błędy', key: 'error', icon: 'error', color: 'text-red-400' }
            ].map(item => (
              <div key={item.key} className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-surface-border p-4 flex items-center gap-3">
                <span className={`material-symbols-outlined ${item.color}`}>{item.icon}</span>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{item.label}</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{events.eventCounts[item.key] || 0}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <TimeSeriesChart
              credentials={credentials}
              eventType="conversion_complete"
              title="Konwersje"
              icon="swap_horiz"
              color="#3b82f6"
            />
            <TimeSeriesChart
              credentials={credentials}
              eventType="download"
              title="Pobrania"
              icon="download"
              color="#10b981"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <TimeSeriesChart
              credentials={credentials}
              eventType="page_view"
              title="Wyświetlenia strony"
              icon="visibility"
              color="#a855f7"
            />
            <HotSongsTable credentials={credentials} />
          </div>

          {/* Hourly activity */}
          <div className="mb-8">
            <HourlyActivityChart credentials={credentials} />
          </div>

          {/* Recent songs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <SongTable
              songs={lastDownloaded}
              title="Ostatnio pobrane"
              icon="download"
              timeLabel="Pobrano"
            />
            <SongTable
              songs={lastConverted}
              title="Ostatnio skonwertowane"
              icon="swap_horiz"
              timeLabel="Skonwertowano"
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StatsPage;
